import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Generic admin CRUD table + form. fields: [{key,label,type:text|number|date|select|textarea,options?}]
export default function ErpCrud({ title, subtitle, endpoint, testid, fields, columns }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(endpoint).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };
  const save = async () => {
    try {
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.key];
        if (f.type === "number") v = v === "" || v == null ? 0 : Number(v);
        payload[f.key] = v ?? "";
      });
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div><h1 className="text-2xl font-bold">{title}</h1>{subtitle && <p className="text-slate-500 text-sm">{subtitle}</p>}</div>
        <button data-testid={`${testid}-add`} onClick={openNew} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold inline-flex items-center gap-1"><Plus size={15} /> Add</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>{columns.map((c) => <th key={c.key} className="px-3 py-2 font-semibold whitespace-nowrap">{c.label}</th>)}<th className="px-3 py-2" /></tr></thead>
            <tbody>
              {!rows.length && <tr><td colSpan={columns.length + 1} className="px-3 py-8 text-center text-slate-400">Koi record nahi.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`${testid}-row-${r.id}`} className="border-t border-slate-100">
                  {columns.map((c) => <td key={c.key} className="px-3 py-2 whitespace-nowrap">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`${testid}-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 text-slate-500 hover:text-slate-800"><Pencil size={14} /></button>
                    <button data-testid={`${testid}-del-${r.id}`} onClick={() => del(r.id)} className="p-1.5 text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} — {title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            {fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                <label className="text-xs font-semibold text-slate-500">{f.label}</label>
                {f.type === "select" ? (
                  <select data-testid={`${testid}-f-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1">
                    <option value="">—</option>
                    {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea data-testid={`${testid}-f-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" />
                ) : (
                  <input data-testid={`${testid}-f-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" />
                )}
              </div>
            ))}
          </div>
          <button data-testid={`${testid}-save`} onClick={save} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold">Save</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
